import React, { useState, useCallback, useEffect, useRef } from 'react';

import {
  LegacyBox,
  TextField,
  LegacyText,
  Select,
  ArrowForwardIcon,
  useRadioGroupContext,
  RadioGroupOption,
} from '@gogame/components';

import {
  ACTION_DECREASE,
  ACTION_INCREASE,
  TYPE_PERCENT,
  TYPE_CURRENCY,
} from '../constants';

export const Preview = ({ initialValue, value, unit }) => {
  return (
    <LegacyBox
      align={'center'}
      gutter={16}
      style={{ padding: '32px 8px 8px 8px' }}
    >
      <LegacyBox vertical={true} gutter={4}>
        <LegacyText variant={'caption'} intensity={200}>
          Current {unit}
        </LegacyText>
        <LegacyText>{initialValue}</LegacyText>
      </LegacyBox>
      <ArrowForwardIcon color={'action'} />
      <LegacyBox vertical={true} gutter={4}>
        <LegacyText variant={'caption'} intensity={200}>
          New {unit}
        </LegacyText>
        <LegacyText>{value}</LegacyText>
      </LegacyBox>
    </LegacyBox>
  );
};

const actions = [
  { id: ACTION_INCREASE, name: 'Increase' },
  { id: ACTION_DECREASE, name: 'Decrease' },
];

const types = [
  { id: TYPE_CURRENCY, name: '$' },
  { id: TYPE_PERCENT, name: '%' },
];

export const AmountRadio = ({
  value,
  onChangeAmount,
  component,
  disabled,
  warning,
  ...props
}) => {
  const radioGroupContext = useRadioGroupContext();

  const handleClick = (e) => {
    radioGroupContext.onChange(e, value);
  };

  return (
    <React.Fragment>
      <LegacyBox align={'center'}>
        <RadioGroupOption
          value={value}
          disabled={disabled}
          label={
            <LegacyBox onClick={handleClick}>
              {React.createElement(component, {
                onChange: onChangeAmount,
                active: radioGroupContext.value === value,
                actionType: value,
                ...props,
              })}
            </LegacyBox>
          }
        />
      </LegacyBox>
      {warning ? (
        <LegacyBox>
          <LegacyText style={{ padding: '32px 9px 0' }}>{warning}</LegacyText>
        </LegacyBox>
      ) : null}
    </React.Fragment>
  );
};

export const SetAmount = ({
  initialValue = '',
  onChange,
  active,
  actionType,
  error,
}) => {
  const [amount, setAmount] = useState(initialValue);
  const onChangeRef = useRef();
  const readyRef = useRef(false);
  onChangeRef.current = onChange;

  const handleChangeAmount = useCallback((_e, a) => {
    setAmount(a);
  }, []);

  useEffect(() => {
    if (readyRef.current) {
      onChangeRef.current({}, { type: actionType, payload: amount });
    } else {
      readyRef.current = true;
    }
  }, [amount]);

  useEffect(() => {
    if (readyRef.current && active) {
      onChangeRef.current({}, { type: actionType, payload: amount });
    }
  }, [active]);

  return (
    <TextField
      type={'currency'}
      label={'Set amount'}
      value={amount}
      placeholder={'0'}
      width={'132px'}
      error={error}
      onChange={handleChangeAmount}
    />
  );
};

export const UpdateAmount = ({ active, unit, onChange, actionType, error }) => {
  const [action, setAction] = useState(ACTION_INCREASE);
  const [type, setType] = useState(TYPE_CURRENCY);
  const [updateValue, setUpdatedValue] = useState('');
  const onChangeRef = useRef();
  const readyRef = useRef(false);

  onChangeRef.current = onChange;

  const handleChangeAction = useCallback((_e, a) => {
    setAction(a);
  }, []);
  const handleChangeType = useCallback((_e, a) => {
    setType(a);
  }, []);

  const handleChangeUpdateValue = useCallback((_e, v) => {
    setUpdatedValue(v);
  }, []);

  useEffect(() => {
    if (readyRef.current) {
      onChangeRef.current(
        {},
        {
          type: actionType,
          payload: {
            action,
            type,
            updateValue,
          },
        }
      );
    } else {
      readyRef.current = true;
    }
  }, [action, type, updateValue]);

  useEffect(() => {
    if (readyRef.current && active) {
      onChangeRef.current(
        {},
        {
          type: actionType,
          payload: {
            action,
            type,
            updateValue,
          },
        }
      );
    }
  }, [active]);

  return (
    <LegacyBox gutter={16} align={'center'}>
      <Select
        withFilter={false}
        value={action}
        onChange={handleChangeAction}
        options={actions}
        width={'132px'}
      />
      <LegacyText>{unit} by</LegacyText>
      <TextField
        type={type === TYPE_CURRENCY ? 'currency' : 'percent'}
        width={'100px'}
        placeholder={'0'}
        value={updateValue}
        onChange={handleChangeUpdateValue}
        error={error}
      />
      <Select
        withFilter={false}
        value={type}
        width={'80px'}
        onChange={handleChangeType}
        options={types}
      />
    </LegacyBox>
  );
};
